import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client.ts';
import type { OAuthProvider, PlanDefinition, PlanId } from '@shared/constants/index.ts';

/**
 * Cloudflare Turnstile widget (spec §6.5).
 *
 * The Worker rejects registration, password reset and repeated-failure logins
 * without a verified token, so the browser has to actually render the challenge
 * and send its result — a server-side check alone would just lock everyone out.
 *
 * The script is loaded once per page in explicit-render mode, so the token can
 * be handed to a JSON request rather than only to a form post.
 */

interface TurnstileApi {
  render: (
    element: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      /** Turnstile hands this the widget's error code, e.g. `400020`. */
      'error-callback'?: (code?: string) => void;
      theme?: 'light' | 'dark' | 'auto';
    },
  ) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    onTurnstileReady?: () => void;
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let scriptPromise: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Allow a later attempt to retry rather than caching the failure forever.
      scriptPromise = null;
      reject(new Error('Could not load the human-verification widget.'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export interface AppConfig {
  appName: string;
  /** The label the quote credit shows, e.g. "blastoclean.com". */
  brandLabel: string;
  environment: string;
  turnstileSiteKey: string | null;
  /**
   * Whether this deployment has SMS credentials at all. A company's own
   * text-reminder switch is meaningless without it, so Settings reads this to
   * decide between offering the toggle and explaining why it cannot.
   */
  smsAvailable: boolean;

  /** Trial length this deployment actually grants (TRIAL_DAYS, default 14). */
  trialDays: number;

  /**
   * Whether this deployment offers "Connect with Stripe" (Stripe Connect
   * OAuth). Settings shows the one-click link flow when it does, and the
   * pasted-key form when it does not.
   */
  stripeConnectAvailable: boolean;

  /**
   * Whether this deployment offers "Connect your Twilio account" (Twilio
   * Connect). Settings → Texting shows the one-click button when it does, and
   * only the pasted-credentials form when it does not.
   */
  twilioConnectAvailable: boolean;

  /**
   * What this deployment charges for the app, in its own currency — the one
   * the Stripe prices are denominated in, which need not be the currency any
   * given company bills its own customers in. `plans` is the full tier
   * catalog (Starter/Pro/Business), mirroring the shared PLANS table.
   */
  plan: {
    currencyCode: string;
    plans: Readonly<Record<PlanId, PlanDefinition>>;
  };

  /**
   * Whether this deployment is a demonstration: no subscription billing, a
   * short access window, and not fit to run a business on. Read before anyone
   * signs in, because that is when someone decides whether to put real work
   * into it.
   */
  demoMode: boolean;
  /** Days a demo account gets from signup. Only meaningful with `demoMode`. */
  demoExpirationDays: number;

  /**
   * Social sign-in providers whose credentials this deployment actually has.
   * The sign-in, registration and Settings pages render a button per entry and
   * nothing at all when it is empty, so an unconfigured provider is invisible
   * rather than a button that fails.
   */
  oauthProviders: OAuthProvider[];

  /**
   * Whether this deployment has an address provider configured
   * (ADDRESS_AUTOCOMPLETE_PROVIDER). The property form reads it to decide
   * between a suggesting address box and a plain one, so a deployment with no
   * provider spends no requests finding that out a keystroke at a time.
   */
  addressAutocomplete: boolean;

  /**
   * Whether the in-app help assistant can answer here — a model key and a
   * documentation source, both configured server-side. The layout renders the
   * help launcher only when this is true.
   */
  helpAssistant: boolean;

  /**
   * The origins a help answer's article links may point at. The panel makes
   * only these clickable — model output is untrusted, and the prompt's
   * "link only the documentation" is an instruction, not an enforcement
   * boundary.
   */
  helpLinkOrigins: string[];
  /**
   * The address of this deployment's MCP server — what a person pastes into
   * ChatGPT or Claude to connect them (Settings → ChatGPT & Claude).
   */
  mcpServerUrl: string;
  /**
   * The documentation site's base URL, e.g. `https://www.blastoclean.com`, for
   * linking a screen to its guide; null on a deployment with no docs site.
   */
  helpDocsUrl: string | null;
}

/** Public configuration the sign-in pages need before anyone is signed in. */
export function useAppConfig() {
  return useQuery({
    queryKey: ['app-config'],
    queryFn: () => api.get<AppConfig>('/config'),
    staleTime: Infinity,
    retry: 1,
  });
}

export function Turnstile({
  siteKey,
  onToken,
  /** Bump to clear a spent token after a failed submission. */
  resetSignal = 0,
}: {
  siteKey: string;
  onToken: (token: string | null) => void;
  resetSignal?: number;
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  /**
   * The code Turnstile refused with, when it gave one.
   *
   * It is the whole diagnosis and it used to be thrown away: `400020` is a
   * sitekey Cloudflare does not know (a widget deleted or recreated), `110200`
   * is a hostname missing from the widget's list, and the two are fixed in
   * different places. Showing it puts that in a screenshot instead of in a
   * console nobody has open.
   */
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  const labelId = useId();

  useEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  useEffect(() => {
    let canceled = false;

    void loadTurnstile()
      .then(() => {
        if (canceled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => {
            setStatus('ready');
            // A retry that succeeds takes the failure message with it.
            setErrorCode(null);
            onTokenRef.current(token);
          },
          'expired-callback': () => onTokenRef.current(null),
          'error-callback': (code) => {
            console.error('turnstile_widget_error', { code: code ?? null, siteKey });
            setStatus('unavailable');
            setErrorCode(typeof code === 'string' && code ? code : null);
            onTokenRef.current(null);
          },
        });
        setStatus('ready');
      })
      .catch(() => {
        // A blocked or unreachable script must not become a dead end. The form
        // stays submittable and the Worker decides — it is the only side that
        // knows whether a token is actually required.
        setStatus('unavailable');
        onTokenRef.current(null);
      });

    return () => {
      canceled = true;
      const widgetId = widgetIdRef.current;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
      widgetIdRef.current = null;
    };
  }, [siteKey]);

  useEffect(() => {
    if (resetSignal === 0) return;
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      onTokenRef.current(null);
    }
  }, [resetSignal]);

  return (
    <div className="mb-4">
      <p id={labelId} className="field-label">
        {t('turnstile.label')}
      </p>
      <div ref={containerRef} aria-labelledby={labelId} />
      {status === 'unavailable' ? (
        <p className="field-hint" role="status">
          {t('turnstile.unavailable')}
          {errorCode ? ` ${t('turnstile.errorCode', { code: errorCode })}` : ''}
        </p>
      ) : null}
    </div>
  );
}
